import * as React from "react"
import styled from "styled-components"
import { IdentifiedLowerRate, ILRState, RateCheckInfo } from "../types"
import { desktop, mobile } from "components/_blueprint/Layout"
import { ToCurrency } from "lib/CurrencyFormatter"
import { formatAppointmentDate } from "lib/FormatDate"
import LenderIcon from "../LenderIcon"

type Props = {
  identifiedLowerRate?: IdentifiedLowerRate | null
  rateCheck: RateCheckInfo
}

export const RateCheckSummary = ({ identifiedLowerRate, rateCheck }: Props) => {
  const {
    LenderName,
    Cashback,
    FreeLegalFees,
    ValuationFee,
    TrueCostOverInitialPeriod,
    InitialMonthlyPayment,
    InitialMonthlyPaymentIncludingFees,
  } = rateCheck?.customerProductDetails || {}

  const { mortgageClass, addFeesToLoan } = rateCheck?.productInput || {}

  const ilrMonthlyPayment = addFeesToLoan
    ? ToCurrency(identifiedLowerRate?.productDetails?.InitialMonthlyPaymentIncludingFees)
    : ToCurrency(identifiedLowerRate?.productDetails?.InitialMonthlyPayment)

  const monthlyPayment = addFeesToLoan
    ? ToCurrency(InitialMonthlyPaymentIncludingFees)
    : ToCurrency(InitialMonthlyPayment)
  const formattedCashback = ToCurrency(Cashback)

  const totalSavings =
    TrueCostOverInitialPeriod - identifiedLowerRate?.productDetails?.TrueCostOverInitialPeriod
  const formattedRateCheckDate = formatAppointmentDate(new Date(rateCheck?.lastChecked))
  const totalMonthlySavings = monthlyPayment - ilrMonthlyPayment
  const formattedTotalSavings = ToCurrency(totalSavings)
  const formattedTotalMonthlySavings = ToCurrency(totalMonthlySavings)

  const completionCutoff = rateCheck?.status === "stopped-completion-cutoff"

  return (
    <SummaryWrapper bg={completionCutoff}>
      <SummaryProductDetails>
        <ProductDetailsWrapper>
          <SummaryIcon bg={completionCutoff}>
            <LenderIcon lenderName={LenderName} />
          </SummaryIcon>
          <ProductExtrasWrapper>
            <ProductExtras bg={completionCutoff}>
              <ExtrasList aria-label="Extras">
                {
                  <ExtraPill>
                    <span>{mortgageClass}</span>
                  </ExtraPill>
                }
                {Cashback > 0 && (
                  <ExtraPill>
                    <span>{formattedCashback} Cashback</span>
                  </ExtraPill>
                )}
                {FreeLegalFees && (
                  <ExtraPill>
                    <span>Free standard legal costs</span>
                  </ExtraPill>
                )}
                {ValuationFee === 0 && (
                  <ExtraPill>
                    <span>Free valuation</span>
                  </ExtraPill>
                )}
              </ExtrasList>
            </ProductExtras>
          </ProductExtrasWrapper>
        </ProductDetailsWrapper>
      </SummaryProductDetails>
      {rateCheck.lastChecked && (
        <SummaryCheckDateMobile>
          Last rate check:<span>{formattedRateCheckDate}</span>
        </SummaryCheckDateMobile>
      )}
      {identifiedLowerRate?.status === ILRState.Progressing &&
        totalMonthlySavings > 0 &&
        totalSavings > 0 && (
          <SummaryMessage>
            This new deal would <span>save you {formattedTotalMonthlySavings}</span> a month. You’d{" "}
            <span>save {formattedTotalSavings} in interest</span> over the fixed period, compared to
            your current rate.
          </SummaryMessage>
        )}
      {identifiedLowerRate?.status === ILRState.Expired && (
        <AlertSummaryMessage>
          Unfortunately this new rate is no longer available. We will continue monitoring the rates
          from {LenderName} and will notify you by email and SMS if rates lower again.
        </AlertSummaryMessage>
      )}
      {identifiedLowerRate?.status === ILRState.Cancelled && (
        <AlertSummaryMessage>
          Unfortunately your application for the new rate with {LenderName} has been declined. Your
          Case Manager will be in contact with you to provide you with more detail.
        </AlertSummaryMessage>
      )}
    </SummaryWrapper>
  )
}

const SummaryWrapper = styled.div<{ bg: boolean }>(({ bg }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  width: "100%",
  gap: "8px",
  [mobile]: {
    backgroundColor: bg ? "white" : "transparent",
    padding: bg ? "0.5rem" : 0,
    borderRadius: bg ? "8px" : 0,
  },
}))

const SummaryProductDetails = styled.div({
  display: "flex",
  flexDirection: "column",
  justifyContent: "start",
  alignItems: "start",
  gap: "32px",
  flex: 5,

  [desktop]: {
    justifyContent: "end",
    alignItems: "end",
  },
})

const ProductDetailsWrapper = styled.div({
  display: "flex",
  justifyContent: "space-between",
  width: "100%",
})

const ProductExtrasWrapper = styled.div`
  display: flex;
  /* flex: 100%; */
  flex-wrap: wrap;
  overflow: hidden;
  justify-content: end;
`

const SummaryIcon = styled.div<{ bg: boolean }>(({ bg }) => ({
  display: "flex",
  alignItems: "center",
  width: "fit-content",
  backgroundColor: bg ? "white" : "transparent",
  padding: bg ? "0.5rem" : 0,
  borderRadius: bg ? "8px" : 0,
  marginRight: "10px",
}))

const ProductExtras = styled.div<{ bg: boolean }>(({ bg }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "end",
  width: "fit-content",
  backgroundColor: bg ? "white" : "transparent",
  padding: bg ? "0.5rem" : 0,
  borderRadius: bg ? "8px" : 0,
  marginRight: "10px",
}))

const ExtrasList = styled.ul({
  display: "flex",
  borderRadius: "16px",
  flexWrap: "wrap",
  padding: 0,
  gap: "8px",

  [mobile]: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: "8px",
  },
})

const ExtraPill = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  border-radius: 24px;
  padding: 12px 8px;
  background: #d3ddf9;

  & > span {
    font-size: 12px;
  }
`

const SummaryCheckDateMobile = styled.div({
  display: "flex",
  marginTop: "1rem",
  justifyContent: "start",
  ["& > span"]: {
    marginLeft: "1rem",
  },
  [desktop]: {
    display: "none",
  },
})

const SummaryMessage = styled.div`
  background: #eef5f3;
  border-radius: 16px;
  padding: 32px;
  border: 1px solid #d4efee;

  & > span {
    font-weight: 600;
  }
`

const AlertSummaryMessage = styled.div`
  background: #faf1ee;
  border-radius: 16px;
  padding: 32px;
  border: 1px solid #f2d0dc;

  & > span {
    font-weight: 600;
  }
`
